import { Injectable } from '@angular/core';

import { KnowledgeBaseCollection } from 'src/models/knowledge-base-collection.model';

import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class KnowledgeBaseCollectionsService {
  private route = '/knowledge-base/collections';

  constructor(private api: ApiService) {}

  // Using Promise<any> for list response type until PaginatedResponse is located
  async list(params: Record<string, any> = {}, page = 1, limit = 50): Promise<any> {
    const queryParams = { ...params, page: String(page), limit: String(limit) };

    return this.api.postPromise<any>(`${this.route}/list`, queryParams);
  }

  async getAll(params: Record<string, any> = {}): Promise<KnowledgeBaseCollection[]> {
    let allCollections: KnowledgeBaseCollection[] = [];
    let page = 1;
    const limit = 100;
    let response: any;

    do {
      // Pass specific params for getAll if needed, e.g., only root items
      const listParams = { ...params };
      response = await this.list(listParams, page, limit);
      if (response?.data) {
        allCollections = allCollections.concat(response.data);
      }
      page++;
    } while (response?.data?.length > 0 && response?.total && allCollections.length < response.total);

    return allCollections;
  }

  async getById(id: string): Promise<KnowledgeBaseCollection> {
    return this.api.getPromise<KnowledgeBaseCollection>(`${this.route}/${id}`);
  }

  async create(data: Partial<KnowledgeBaseCollection>): Promise<KnowledgeBaseCollection> {
    const payload = { ...data };
    delete payload.id;
    delete payload.createdAt;
    delete payload.updatedAt;
    return this.api.postPromise<KnowledgeBaseCollection>(this.route, payload);
  }

  async update(id: string, data: Partial<KnowledgeBaseCollection>): Promise<KnowledgeBaseCollection> {
    const payload = { ...data };
    if (payload.parentId === undefined) {
      delete payload.parentId;
    } else {
      payload.parentId = payload.parentId === '' ? null : payload.parentId;
    }
    delete payload.id;
    delete payload.createdAt;
    delete payload.updatedAt;
    return this.api.patchPromise<KnowledgeBaseCollection>(`${this.route}/${id}`, payload);
  }

  async delete(id: string): Promise<void> {
    return this.api.deletePromise<void>(`${this.route}/${id}`);
  }

  // --- Helper methods (no changes needed here) ---

  buildTree(collections: KnowledgeBaseCollection[], parentId: string | null = null): any[] {
    return collections
      .filter((collection) => collection.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((collection) => ({
        ...collection,
        children: this.buildTree(collections, collection.id),
      }));
  }

  async getCollectionPath(
    collectionId: string | null,
    allCollections?: KnowledgeBaseCollection[],
  ): Promise<{ id: string; name: string }[]> {
    if (!collectionId) {
      return [];
    }

    const collections = allCollections ?? (await this.getAll());
    const path: { id: string; name: string }[] = [];
    let currentId: string | null = collectionId;

    let iterations = 0;
    const maxIterations = 100;

    while (currentId && iterations < maxIterations) {
      const currentCollection = collections.find((c) => c.id === currentId);
      if (currentCollection) {
        path.unshift({ id: currentCollection.id, name: currentCollection.name });
        currentId = currentCollection.parentId;
      } else {
        console.warn(`Collection path incomplete: Could not find collection with ID ${currentId}`);
        currentId = null;
      }
      iterations++;
    }

    if (iterations >= maxIterations) {
      console.warn(`getCollectionPath reached max iterations for ID ${collectionId}. Possible circular reference?`);
    }

    return path;
  }
}
